// /home/cockskiz/new.greatlysocial.com/frontend/src/pages/dashboard/post/facebook/FacebookPostService.js
import axios from 'axios';

/**
 * publishFacebookPost
 * Sends a request to your backend to publish a post to a connected Facebook Page.
 *
 * @param {Object} opts
 * ─────────────────────────────────────────────────────────────
 * opts.pageId   (string)   — Facebook Page ID
 * opts.message  (string)   — Caption / body text
 * opts.mediaUrl (string)   — Public URL to photo or video (optional)
 * opts.isVideo  (boolean)  — true if mediaUrl points to a video
 * opts.format   (string)   — 'feed' | 'story' | 'reel'  (default: 'feed')
 *
 * Returns: server JSON with success / error info.
 */
export async function publishFacebookPost({
  pageId,
  message = '',
  mediaUrl = '',
  isVideo = false,
  format = 'feed',
}) {
  try {
    /* ------------ guard clauses ------------ */
    if (!pageId) {
      throw new Error('Facebook Page ID is required');
    }
    if (!message && !mediaUrl) {
      throw new Error('You must provide either a message or media');
    }
    
    /* ------------ build payload ------------ */
    const payload = { pageId, message, mediaUrl, isVideo, format };
    
    /* ------------ call your backend -------- */
    const res = await axios.post(
      '/api/posting/facebook/publish',
      payload,
      { withCredentials: true }   // session-based auth
    );
    
    console.log('[FB publish] success:', res.data);
    return res.data;
    
  } catch (err) {
    console.error('[FB publish] error:', err);
    
    // Extract the actual error message from various possible locations
    let errorMessage = 'Failed to post to Facebook';
    
    // If this is our custom validation error, preserve it
    if (!err.response && err.message) {
      errorMessage = err.message;
    } else if (err.response?.data) {
      // Check multiple possible error locations in order of preference
      if (err.response.data.error && typeof err.response.data.error === 'string') {
        // If error is a string directly
        errorMessage = err.response.data.error;
      } else if (err.response.data.error?.message) {
        // If error is an object with a message property
        errorMessage = err.response.data.error.message;
      } else if (err.response.data.message) {
        // If there's a message property directly
        errorMessage = err.response.data.message;
      } else if (err.response.data.details) {
        // If there's a details property
        errorMessage = err.response.data.details;
      } else if (err.response.data.error?.error_user_msg) {
        // Facebook API specific error message
        errorMessage = err.response.data.error.error_user_msg;
      } else if (err.response.data.error?.error_message) {
        // Another Facebook API error format
        errorMessage = err.response.data.error.error_message;
      } else if (typeof err.response.data === 'string') {
        // If the entire response data is a string
        errorMessage = err.response.data;
      }
    } else if (err.response?.status) {
      // Handle HTTP status codes
      switch (err.response.status) {
        case 401:
          errorMessage = 'Facebook authentication failed. Please reconnect your account.';
          break;
        case 403:
          errorMessage = 'Facebook permission denied. Check your page permissions.';
          break;
        case 429:
          errorMessage = 'Facebook rate limit exceeded. Please wait before posting again.';
          break;
        case 500:
        case 502:
        case 503:
          errorMessage = 'Facebook server error. Please try again later.';
          break;
        default:
          if (err.message) {
            errorMessage = err.message;
          }
      }
    } else if (err.message) {
      errorMessage = err.message;
    }
    
    // Throw error with the extracted message
    throw new Error(errorMessage);
  }
}

/**
 * Helper function to get user-friendly error messages
 * Can be used by UI components to show even better messages
 */
export function getFacebookErrorMessage(error) {
  const message = error.message || error.toString();
  const lowerMessage = message.toLowerCase();
  
  // Map technical errors to user-friendly messages
  if (lowerMessage.includes('token') || lowerMessage.includes('auth') || lowerMessage.includes('expired')) {
    return {
      title: 'Connection Issue',
      message: 'Your Facebook connection has expired. Please reconnect your account in Settings.',
      action: 'reconnect'
    };
  }
  
  if (lowerMessage.includes('permission') || lowerMessage.includes('scope')) {
    return {
      title: 'Permission Required',
      message: 'Missing Facebook permissions. Please reconnect with all required permissions.',
      action: 'reconnect'
    };
  }
  
  if (lowerMessage.includes('rate limit') || lowerMessage.includes('too many')) {
    return {
      title: 'Slow Down',
      message: 'You\'ve reached Facebook\'s posting limit. Please wait a few minutes before trying again.',
      action: 'wait'
    };
  }
  
  if (lowerMessage.includes('page not found') || lowerMessage.includes('invalid page')) {
    return {
      title: 'Page Not Found',
      message: 'This Facebook Page no longer exists or you\'ve lost access to it.',
      action: 'refresh'
    };
  }
  
  if (lowerMessage.includes('duplicate')) {
    return {
      title: 'Duplicate Content',
      message: 'This content was already posted to Facebook.',
      action: null
    };
  }
  
  if (lowerMessage.includes('video') || lowerMessage.includes('media')) {
    return {
      title: 'Media Issue',
      message: message,
      action: 'check_media'
    };
  }
  
  // Default
  return {
    title: 'Posting Failed',
    message: message,
    action: null
  };
}